import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR } from "@/components/AppTopBar";

const EMPTY_FILTERS = { date_from: "", date_to: "", source_account_id: "", status: "ALL" };

export function WithdrawalHistory({ accounts: sharedAccounts = [], refreshKey = 0, onChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ amount: 0, modal: 0, profit: 0, unclassified: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setAccounts(Array.isArray(sharedAccounts) ? sharedAccounts : []);
  }, [sharedAccounts]);

  const wallets = useMemo(
    () => accounts.filter((a) => a.account_type === "MARKETPLACE_WALLET"),
    [accounts],
  );

  const load = useCallback(async (wantedPage = 1, applied = filters) => {
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams();
      if (applied.date_from) params.set("date_from", applied.date_from);
      if (applied.date_to) params.set("date_to", applied.date_to);
      if (applied.source_account_id) params.set("source_account_id", applied.source_account_id);
      if (applied.status && applied.status !== "ALL") params.set("status", applied.status);
      params.set("page", String(wantedPage));
      params.set("page_size", "50");
      const { data } = await api.get(`/marketplace/withdrawals?${params.toString()}`);
      setRows(data.rows || []);
      setTotals(data.totals || {});
      setPage(data.page || wantedPage);
      setTotalPages(Math.max(1, data.total_pages || 1));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  }, [filters]);

  useEffect(() => { load(1); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => { setPage(1); load(1, filters); };
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
    load(1, EMPTY_FILTERS);
  };

  const reverse = async (row) => {
    if (row.status === "REVERSED") return;
    const ok = window.confirm(
      `Reversal penarikan ini?\n\nWallet: ${row.source_account_name}\nBank: ${row.dest_account_name}\nTotal: ${formatIDR(row.amount)}\n\nDana akan dikembalikan ke wallet asal dengan komposisi persis transaksi awal.`
    );
    if (!ok) return;
    setBusy(true); setError("");
    try {
      await api.post(`/marketplace/withdrawal/${row.id}/reverse`);
      await load(page, filters);
      if (onChanged) onChanged();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel panel--wide" style={{ marginTop: 16 }} data-testid="withdrawal-history-panel">
      <div className="panel-head" style={{ alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 className="panel-title" style={{ marginBottom: 4 }}>History Penarikan Wallet</h2>
          <div className="hint">Filter per wallet asal/toko dan tanggal. Reversal hanya untuk koreksi transaksi yang salah.</div>
        </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))", gap: 10, marginBottom: 12 }}>
        <div><label className="field-label">Dari Tanggal</label><input className="field-input" type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters,date_from:e.target.value})} /></div>
        <div><label className="field-label">Sampai Tanggal</label><input className="field-input" type="date" value={filters.date_to} onChange={(e)=>setFilters({...filters,date_to:e.target.value})} /></div>
        <div><label className="field-label">Wallet Asal / Toko</label><select className="field-input" value={filters.source_account_id} onChange={(e)=>setFilters({...filters,source_account_id:e.target.value})}><option value="">Semua Wallet</option>{wallets.map((w)=><option key={w.account_id} value={w.account_id}>{w.account_name}</option>)}</select></div>
        <div><label className="field-label">Status</label><select className="field-input" value={filters.status} onChange={(e)=>setFilters({...filters,status:e.target.value})}><option value="ALL">Semua</option><option value="COMMITTED">COMMITTED</option><option value="REVERSED">REVERSED</option></select></div>
        <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}><button className="btn-primary" onClick={applyFilters} disabled={busy}>Terapkan</button><button className="btn-ghost" onClick={resetFilters} disabled={busy}>Reset</button></div>
      </div>

      <div className="meta-grid" style={{ marginBottom: 12 }}>
        <div><span className="meta-label">Total Penarikan</span><span className="mono">{formatIDR(totals.amount)}</span></div>
        <div><span className="meta-label">MODAL</span><span className="mono">{formatIDR(totals.modal)}</span></div>
        <div><span className="meta-label">PROFIT</span><span className="mono">{formatIDR(totals.profit)}</span></div>
        <div><span className="meta-label">UNCLASSIFIED</span><span className="mono">{formatIDR(totals.unclassified)}</span></div>
      </div>

      <div className="table-scroll"><table className="data-table" data-testid="withdrawal-history-table">
        <thead><tr><th>Tanggal Penarikan</th><th>Tanggal Posting</th><th>Wallet Asal</th><th>Bank Tujuan</th><th>MODAL</th><th>PROFIT</th><th>UNCLASSIFIED</th><th>Total</th><th>Status</th><th>Ref</th><th>Aksi</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={11} className="empty-cell">Belum ada history penarikan.</td></tr>}
          {rows.map((r) => <tr key={r.id}>
            <td>{r.actual_withdrawal_date ? r.actual_withdrawal_date.split("-").reverse().join("/") : "—"}</td>
            <td>{r.created_at ? new Date(r.created_at).toLocaleString("id-ID") : "—"}</td>
            <td><b>{r.source_account_name || "-"}</b></td>
            <td>{r.dest_account_name || "-"}</td>
            <td className="mono">{formatIDR(r.modal)}</td>
            <td className="mono">{formatIDR(r.profit)}</td>
            <td className="mono">{formatIDR(r.unclassified)}</td>
            <td className="mono"><b>{formatIDR(r.amount)}</b></td>
            <td><span className={`status-pill ${r.status === "COMMITTED" ? "on" : "off"}`}>{r.status}</span>{r.reversed_at && <div className="hint">{new Date(r.reversed_at).toLocaleString("id-ID")}</div>}</td>
            <td className="mono" title={r.withdrawal_key}>{r.withdrawal_key ? r.withdrawal_key.slice(0, 12) : "-"}</td>
            <td>{r.status === "COMMITTED" ? <button className="btn-mini" onClick={()=>reverse(r)} disabled={busy}>Reversal</button> : <span className="hint">Sudah direversal</span>}</td>
          </tr>)}
        </tbody>
      </table></div>

      {totalPages > 1 && <div className="form-actions" style={{ justifyContent:"space-between" }}>
        <button className="btn-ghost" disabled={busy || page <= 1} onClick={()=>load(page-1, filters)}>← Sebelumnya</button>
        <span className="hint">Halaman {page} / {totalPages}</span>
        <button className="btn-ghost" disabled={busy || page >= totalPages} onClick={()=>load(page+1, filters)}>Berikutnya →</button>
      </div>}
    </section>
  );
}
